import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest?.headers['X-Retry']) {
          originalRequest!.headers['X-Retry'] = 'true';

          const refreshToken = sessionStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const response = await this.client.post('/auth/refresh-token', {
                refreshToken,
              });

              sessionStorage.setItem('accessToken', response.data.data.accessToken);
              return this.client(originalRequest!);
            } catch (err) {
              sessionStorage.removeItem('accessToken');
              sessionStorage.removeItem('refreshToken');
              window.location.href = '/login';
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async register(data: any) {
    return this.client.post('/auth/register', data);
  }

  async login(email: string, password: string) {
    return this.client.post('/auth/login', { email, password });
  }

  async getProfile() {
    return this.client.get('/auth/profile');
  }

  async logout() {
    return this.client.post('/auth/logout');
  }

  async forgotPassword(email: string) {
    return this.client.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string) {
    return this.client.post('/auth/reset-password', { token, password });
  }

  // Shipment endpoints
  async createShipment(data: any) {
    return this.client.post('/shipments', data);
  }

  async getShipment(id: string) {
    return this.client.get(`/shipments/${id}`);
  }

  async listShipments(filters?: any, limit?: number, offset?: number) {
    return this.client.get('/shipments', {
      params: { ...filters, limit, offset },
    });
  }

  async updateShipment(id: string, data: any) {
    return this.client.put(`/shipments/${id}`, data);
  }

  async listShipmentComments(id: string) {
    return this.client.get(`/shipments/${id}/comments`);
  }

  async createShipmentComment(id: string, comment: string) {
    return this.client.post(`/shipments/${id}/comments`, { comment });
  }

  async cancelShipment(id: string) {
    return this.client.post('/shipments/bulk', { ids: [id], action: 'cancel' });
  }

  async confirmShipment(id: string) {
    return this.client.post(`/shipments/${id}/confirm`);
  }

  async dispatchShipment(id: string, data: any) {
    return this.client.post(`/shipments/${id}/dispatch`, data);
  }

  async updateTracking(id: string, data: any) {
    return this.client.post(`/shipments/${id}/tracking`, data);
  }

  async deliverShipment(id: string, data: any) {
    return this.client.post(`/shipments/${id}/deliver`, data);
  }

  async getTrackingHistory(id: string) {
    return this.client.get(`/shipments/${id}/tracking`);
  }

  async publicTracking(trackingNumber: string) {
    return this.client.get(`/shipments/track/${trackingNumber}`);
  }

  // Company endpoints
  async createCompany(data: any) {
    return this.client.post('/companies', data);
  }

  async getCompany(id: string) {
    return this.client.get(`/companies/${id}`);
  }

  async listCompanies(limit?: number, offset?: number) {
    return this.client.get('/companies', { params: { limit, offset } });
  }

  async listActiveCompanies() {
    return this.client.get('/companies/active');
  }

  async updateCompany(id: string, data: any) {
    return this.client.put(`/companies/${id}`, data);
  }

  async setCompanyStatus(id: string, status: string) {
    return this.client.patch(`/companies/${id}/status`, { status });
  }

  async deleteCompany(id: string) {
    return this.client.delete(`/companies/${id}`);
  }

  // Branch endpoints
  async createBranch(companyId: string, data: any) {
    return this.client.post(`/branches/${companyId}`, data);
  }

  async getBranch(companyId: string, branchId: string) {
    return this.client.get(`/branches/${companyId}/${branchId}`);
  }

  async listBranches(companyId: string, limit?: number, offset?: number) {
    return this.client.get(`/branches/${companyId}`, { params: { limit, offset } });
  }

  async updateBranch(companyId: string, branchId: string, data: any) {
    return this.client.put(`/branches/${companyId}/${branchId}`, data);
  }

  async updateBranchStatus(companyId: string, branchId: string, status: string) {
    return this.client.patch(`/branches/${companyId}/${branchId}/status`, { status });
  }

  async deleteBranch(companyId: string, branchId: string) {
    return this.client.delete(`/branches/${companyId}/${branchId}`);
  }

  // Payment endpoints
  async createPayment(data: any) {
    return this.client.post('/payments', data);
  }

  async getPayment(id: string) {
    return this.client.get(`/payments/${id}`);
  }

  async listPayments(filters?: any, limit?: number, offset?: number) {
    return this.client.get('/payments', {
      params: { ...filters, limit, offset },
    });
  }

  async completePayment(id: string, data: any) {
    return this.client.post(`/payments/${id}/complete`, data);
  }

  async failPayment(id: string, reason?: string) {
    return this.client.post(`/payments/${id}/fail`, { reason });
  }

  async toggleApiIntegration(companyId: string, enabled: boolean) {
    return this.client.post(`/companies/${companyId}/api-integration/toggle`, { allowApiIntegration: enabled });
  }

  async toggleWebhooks(companyId: string, enabled: boolean) {
    return this.client.post(`/companies/${companyId}/webhooks/toggle`, { allowWebhooks: enabled });
  }

  async updateWebhookUrl(companyId: string, webhookUrl: string | null) {
    return this.client.patch(`/companies/${companyId}/webhook-url`, { webhookUrl });
  }

  // API Key endpoints
  async generateApiKey(companyId: string, data: any) {
    return this.client.post(`/companies/${companyId}/api-keys/generate`, data);
  }

  async listApiKeys(companyId: string) {
    return this.client.get(`/companies/${companyId}/api-keys`);
  }

  async revokeApiKey(companyId: string, keyId: string) {
    return this.client.delete(`/companies/${companyId}/api-keys/${keyId}`);
  }

  async rotateWebhookSecret(companyId: string) {
    return this.client.post(`/companies/${companyId}/webhook-secret/rotate`);
  }

  async clearWebhookSecret(companyId: string) {
    return this.client.delete(`/companies/${companyId}/webhook-secret`);
  }

  // Webhook endpoints
  async listWebhookEvents(companyId: string, limit?: number, offset?: number, filters?: { status?: string; eventType?: string }) {
    return this.client.get(`/webhooks/${companyId}`, { params: { limit, offset, ...filters } });
  }

  async getWebhookEvent(id: string) {
    return this.client.get(`/webhooks/event/${id}`);
  }

  async retryWebhookEvent(id: string) {
    return this.client.post(`/webhooks/event/${id}/retry`);
  }

  // Webhook endpoint management (multi-endpoint)
  async listWebhookEndpoints(companyId: string) {
    return this.client.get(`/webhook-endpoints/${companyId}`);
  }

  async createWebhookEndpoint(companyId: string, data: { name: string; url: string; eventSubscriptions?: string[] | null }) {
    return this.client.post(`/webhook-endpoints/${companyId}`, data);
  }

  async updateWebhookEndpoint(id: string, data: { name?: string; url?: string; status?: 'ACTIVE' | 'DISABLED'; eventSubscriptions?: string[] | null }) {
    return this.client.patch(`/webhook-endpoints/endpoint/${id}`, data);
  }

  async rotateWebhookEndpointSecret(id: string) {
    return this.client.post(`/webhook-endpoints/endpoint/${id}/rotate-secret`);
  }

  async testWebhookEndpoint(id: string) {
    return this.client.post(`/webhook-endpoints/endpoint/${id}/test`);
  }

  async replayWebhookEndpoint(id: string, eventId: string) {
    return this.client.post(`/webhook-endpoints/endpoint/${id}/replay`, { eventId });
  }

  // Export + schedule APIs
  async createExportJob(companyId: string, data: { resource: 'shipments' | 'payments'; format?: 'csv' | 'ndjson'; params?: any }) {
    return this.client.post(`/exports/${companyId}/jobs`, data);
  }

  async listExportJobs(companyId: string, limit?: number, offset?: number) {
    return this.client.get(`/exports/${companyId}/jobs`, { params: { limit, offset } });
  }

  async downloadExportJob(jobId: string) {
    return this.client.get(`/exports/job/${jobId}/download`, { responseType: 'blob' });
  }

  async createExportSchedule(companyId: string, data: { resource: 'shipments' | 'payments'; format?: 'csv' | 'ndjson'; frequencyMinutes: number; params?: any }) {
    return this.client.post(`/exports/${companyId}/schedules`, data);
  }

  async listExportSchedules(companyId: string) {
    return this.client.get(`/exports/${companyId}/schedules`);
  }

  async updateExportSchedule(id: string, data: { enabled?: boolean; frequencyMinutes?: number; params?: any }) {
    return this.client.patch(`/exports/schedules/${id}`, data);
  }

  // BI connector sync
  async syncShipments(params?: { since?: string; limit?: number; companyId?: string }) {
    return this.client.get('/sync/shipments', { params });
  }

  async syncPayments(params?: { since?: string; limit?: number; companyId?: string }) {
    return this.client.get('/sync/payments', { params });
  }

  // User endpoints
  async listUsers(companyId?: string, limit?: number, offset?: number, search?: string, role?: string) {
    return this.client.get('/users', { params: { companyId, limit, offset, search, role } });
  }

  async createUser(data: any) {
    return this.client.post('/users', data);
  }

  async updateUser(id: string, data: any) {
    return this.client.put(`/users/${id}`, data);
  }

  async deleteUser(id: string) {
    return this.client.delete(`/users/${id}`);
  }

  async getUser(id: string) {
    return this.client.get(`/users/${id}`);
  }

  async updateProfile(data: { firstName?: string; lastName?: string; phone?: string; currentPassword?: string; password?: string }) {
    return this.client.put('/auth/profile', data);
  }

  async getShipmentPayment(shipmentId: string) {
    return this.client.get(`/payments/shipment/${shipmentId}`);
  }

  async refundPayment(id: string, reason?: string) {
    return this.client.post(`/payments/${id}/refund`, { reason });
  }

  async submitPaymentDetails(id: string, data: { paymentMethod?: string; transactionReference?: string; notes?: string }) {
    return this.client.patch(`/payments/${id}/details`, data);
  }

  async updatePaymentStatus(shipmentId: string, data: { status: string; transactionReference?: string; notes?: string }) {
    return this.client.patch(`/shipments/${shipmentId}/payment-status`, data);
  }

  async getDashboardStats(params?: { dateFrom?: string; dateTo?: string }) {
    return this.client.get('/stats/dashboard', { params });
  }

  async getCompaniesBreakdown(params?: { dateFrom?: string; dateTo?: string }) {
    return this.client.get('/stats/companies', { params });
  }

  async getReportStats(params?: { companyId?: string; status?: string; dateFrom?: string; dateTo?: string }) {
    return this.client.get('/stats/reports', { params });
  }

  async getAuditLogs(params?: { action?: string; actorRole?: string; targetType?: string; companyId?: string; dateFrom?: string; dateTo?: string; search?: string; limit?: number; offset?: number }) {
    return this.client.get('/audit-logs', { params });
  }

  async getSettings() {
    return this.client.get('/settings');
  }

  async updateSettings(data: Record<string, string>) {
    return this.client.put('/settings', data);
  }

  async getBillingPlans() {
    return this.client.get('/billing/plans');
  }

  async createBillingPlan(data: any) {
    return this.client.post('/billing/plans', data);
  }

  async deleteBillingPlan(id: string) {
    return this.client.delete(`/billing/plans/${id}`);
  }

  async updateBillingPlan(id: string, data: any) {
    return this.client.put(`/billing/plans/${id}`, data);
  }

  async getBillingOverview() {
    return this.client.get('/billing/overview');
  }

  async assignCompanyPlan(companyId: string, planId: string, billingNotes?: string) {
    return this.client.patch(`/billing/companies/${companyId}/plan`, { planId, billingNotes });
  }

  async updateCompanyNotes(companyId: string, data: { internalNotes?: string; billingNotes?: string }) {
    return this.client.patch(`/billing/companies/${companyId}/notes`, data);
  }

  async getSystemHealth() {
    return this.client.get('/system/health');
  }

  async getSystemLogs(type?: 'error' | 'combined', lines?: number) {
    return this.client.get('/system/logs', { params: { type, lines } });
  }

  async bulkShipmentAction(ids: string[], action: 'confirm' | 'cancel') {
    return this.client.post('/shipments/bulk', { ids, action });
  }

  async exportShipmentsCsv(filters?: { status?: string; dateFrom?: string; dateTo?: string }) {
    return this.client.get('/shipments/export', {
      params: filters,
      responseType: 'blob',
    });
  }

  async listShipmentsSearch(filters?: any, limit?: number, offset?: number) {
    return this.client.get('/shipments', {
      params: { ...filters, limit, offset },
    });
  }

  async listTransporters() {
    return this.client.get('/users/transporters');
  }

  async flagShipmentException(id: string, data: { type: string; description: string; location?: string }) {
    return this.client.post(`/shipments/${id}/exception`, data);
  }

  async deliverShipmentWithPOD(id: string, formData: FormData) {
    return this.client.post(`/shipments/${id}/deliver`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async requestReturn(id: string, returnReason: string) {
    return this.client.post(`/shipments/${id}/return-request`, { returnReason });
  }

  async approveReturn(id: string, approved: boolean, notes?: string) {
    return this.client.post(`/shipments/${id}/return-approve`, { approved, notes });
  }

  async getManifest(params?: { transporterId?: string; date?: string }) {
    return this.client.get('/shipments/manifest', { params });
  }

  getWaybillUrl(shipmentId: string): string {
    return `${API_BASE_URL}/shipments/${shipmentId}/waybill`;
  }

  async getTransporterStats() {
    return this.client.get('/shipments/transporter-stats');
  }

  async getTowns() {
    return this.client.get('/towns');
  }

  async getPublicPricing() {
    return this.client.get('/towns/pricing');
  }

   async getPaymentStats(companyId: string) {
     return this.client.get(`/payments/company/${companyId}/stats`);
   }

   // Inventory endpoints
   async getInventory(filters?: any, limit?: number, offset?: number) {
     return this.client.get('/inventory', {
       params: { ...filters, limit, offset },
     });
   }

   async getInventoryItem(id: string) {
     return this.client.get(`/inventory/${id}`);
   }

   async createInventoryItem(data: any) {
     return this.client.post('/inventory', data);
   }

   async updateInventoryItem(id: string, data: any) {
     return this.client.put(`/inventory/${id}`, data);
   }

   async deleteInventoryItem(id: string) {
     return this.client.delete(`/inventory/${id}`);
   }

   async getInventoryTransactions(inventoryId?: string, filters?: any, page?: number, limit?: number) {
     if (inventoryId) {
       return this.client.get(`/inventory/${inventoryId}/transactions`, {
         params: { ...filters, page, limit },
       });
     }
     return this.client.get('/inventory/transactions', {
       params: { ...filters, page, limit },
     });
   }

   async createInventoryTransaction(data: any) {
     return this.client.post('/inventory/transactions', data);
   }

   async getLowStockAlerts(filters?: any) {
     return this.client.get('/inventory/low-stock/alerts', {
       params: filters,
     });
   }
 }

export default new ApiClient();
